// Browser-side Argon2id (D-21, ANON-03). NEVER import from apps/api.
// Derives 64 bytes from protocolo (salt) + passphrase using hash-wasm Argon2id.
// First 32 bytes = authHash (sent to server). Last 32 bytes = kekRaw (stays in browser).
import { argon2id } from 'hash-wasm';

export interface Argon2Params {
  /** Memory cost in KiB. Default: 65536 = 64 MiB (D-21). */
  memoryCost: number;
  /** Time cost iterations. Default: 3 (D-21). */
  timeCost: number;
  /** Parallelism. Default: 1 (D-21, P1-F: >1 undefined in browser). */
  parallelism: number;
  /** Hash output length in bytes. Default: 64 = 32 auth + 32 KEK. */
  hashLength: number;
}

export const DEFAULT_ARGON2_PARAMS: Argon2Params = {
  memoryCost: 65536, // 64 MiB
  timeCost: 3,
  parallelism: 1,
  hashLength: 64,
};

/**
 * Derives 64 bytes from protocolo (salt) + passphrase using hash-wasm Argon2id.
 * Salt is the protocolo string UTF-8 encoded (Argon2id requires salt >= 8 bytes;
 * protocolo is >= 12 chars).
 */
export async function deriveKeyMaterial(
  protocolo: string,
  passphrase: string,
  params: Argon2Params = DEFAULT_ARGON2_PARAMS,
): Promise<Uint8Array> {
  const salt = new TextEncoder().encode(protocolo);
  const result = await argon2id({
    password: passphrase,
    salt,
    parallelism: params.parallelism,
    iterations: params.timeCost,
    memorySize: params.memoryCost,
    hashLength: params.hashLength,
    outputType: 'binary',
  });
  return new Uint8Array(result);
}

/**
 * Splits the 64-byte output into:
 * - authHash[0..31] — sent to server for lookup (ANON-03: server never sees cleartext)
 * - kekRaw[32..63] — stays in browser, used to wrap/unwrap the DEK
 */
export function splitKeyMaterial(material: Uint8Array): {
  authHash: Uint8Array;
  kekRaw: Uint8Array;
} {
  if (material.length !== 64) {
    throw new Error(`Expected 64 bytes, got ${String(material.length)}`);
  }
  return {
    authHash: material.slice(0, 32),
    kekRaw: material.slice(32, 64),
  };
}
